import {
    saveTokens,
    getAccessToken,
    clearTokens,
    isTokenExpired,
    getUserData,
} from '../utils/tokenStorage';
import config from '../config';

const AUTH_ENDPOINTS = {
    LOGIN: `${config.API_BASE_URL}/api/v1/auth/login`,
    REGISTER: `${config.API_BASE_URL}/api/v1/auth/register`,
    REFRESH: `${config.API_BASE_URL}/api/v1/auth/refresh`,
    LOGOUT: `${config.API_BASE_URL}/api/v1/auth/logout`,
    ME: `${config.API_BASE_URL}/api/v1/auth/me`,
    UPDATE_PASSWORD: `${config.API_BASE_URL}/api/v1/auth/update-password`,
    REQUEST_PASSWORD_RESET: `${config.API_BASE_URL}/api/v1/auth/request-password-reset`,
    VERIFY_RESET_TOKEN: `${config.API_BASE_URL}/api/v1/auth/verify-reset-token`,
    RESET_PASSWORD: `${config.API_BASE_URL}/api/v1/auth/reset-password`,
    REQUEST_VERIFICATION_CODE: `${config.API_BASE_URL}/api/v1/auth/request-verification-code`,
    VERIFY_EMAIL: `${config.API_BASE_URL}/api/v1/auth/verify-email`,
};

let refreshTokenPromise = null;

export const requestVerificationCode = async (email) => {
  const url = AUTH_ENDPOINTS.REQUEST_VERIFICATION_CODE;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Request failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Request verification code error:', error);
    throw error;
  }
};

export const login = async (usernameOrEmail, password) => {
    try {
        const formData = new FormData();
        formData.append('username', usernameOrEmail);
        formData.append('password', password);
        
        const response = await fetch(AUTH_ENDPOINTS.LOGIN, {
            method: 'POST',
            body: formData,
            credentials: 'include', // Important: Receives HttpOnly cookie
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: `HTTP error ${response.status}` }));
            throw new Error(error.detail || 'Login failed');
        }

        const authData = await response.json();
        saveTokens(authData);
        return authData;
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
};

export const register = async (userData) => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.REGISTER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
      credentials: 'include',
    });

    if (response.ok) {
      const responseBodyText = await response.text();
      return responseBodyText ? JSON.parse(responseBodyText) : { success: true };
    }

    const errorData = await response.json().catch(() => ({ detail: `HTTP error ${response.status}` }));
    throw new Error(errorData.detail || errorData.message || 'Registration failed');
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const verifyEmail = async (code) => {
  const url = AUTH_ENDPOINTS.VERIFY_EMAIL;
  try {
    const response = await authFetch(url, {
      method: 'POST',
      body: { code },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Verification failed.');
    }
    return await response.json();
  } catch (error) {
    console.error('Email verification error:', error);
    throw error;
  }
};

export const logout = async () => {
    try {
        // We must call the backend to clear the HttpOnly cookie
        await fetch(AUTH_ENDPOINTS.LOGOUT, {
            method: 'POST',
            credentials: 'include'
        });
    } catch (e) {
        console.warn("Server logout failed, clearing local state anyway.", e);
    } finally {
        clearTokens();
    }
};

export const refreshToken = async () => {
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }
    refreshTokenPromise = (async () => {
        try {
            const response = await fetch(AUTH_ENDPOINTS.REFRESH, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({})
            });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Changed from console.error to console.debug to reduce noise
                    console.debug(`[authService] Refresh token rejected by server (${response.status}). Logging out.`);
                    clearTokens();
                }
                let errorMessage = 'Token refresh failed';
                try {
                    const error = await response.json();
                    errorMessage = error.detail || errorMessage;
                } catch (e) {
                    errorMessage = `${errorMessage}: ${response.status}`;
                }
                throw new Error(errorMessage);
            }
            const authData = await response.json();
            saveTokens(authData);
            return authData;
        } catch (error) {
            // Only log actual errors, not expected auth failures
            const isAuthError = error.message?.includes('401') || 
                                error.message?.includes('403') ||
                                error.message?.toLowerCase().includes('token refresh failed');
            
            if (!isAuthError) {
                console.error('Token refresh error:', error);
            }
            throw error;
        } finally {
            refreshTokenPromise = null;
        }
    })();
    return refreshTokenPromise;
};

export const getCurrentUser = async () => {
    try {
        if (isTokenExpired()) {
            await refreshToken();
        }
        const token = getAccessToken();
        if (!token) { throw new Error('No access token available after check/refresh'); }

        const response = await fetch(AUTH_ENDPOINTS.ME, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
        });

        if (!response.ok) {
            let errorMessage = 'Failed to get user information';
            if (response.status === 401) {
                clearTokens();
                errorMessage = 'Unauthorized: Invalid token.';
            }
            else {
                try {
                    const error = await response.json();
                    errorMessage = error.detail || errorMessage;
                } catch (e) {
                    errorMessage = `${errorMessage}: ${response.status}`;
                }
            }
            throw new Error(errorMessage);
        }
        return await response.json();
    } catch (error) {
        console.error('Get current user error:', error);
        if (error.message.includes('Unauthorized') || error.message.includes('No access token')) {
            clearTokens();
        }
        throw error;
    }
};

export const authFetch = async (url, options = {}) => {
    const fullUrl = url.startsWith('http') ? url : `${config.API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    
    try {
        // Try to refresh if expired, but don't fail hard — just proceed without auth
        if (isTokenExpired()) {
            try {
                await refreshToken();
            } catch (refreshError) {
                console.debug("[authFetch] No valid token, proceeding without auth.");
            }
        }

        const token = getAccessToken();
        const headers = { ...(options.headers || {}) };

        // Attach token if available, otherwise make the request unauthenticated
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        let body = options.body;
        if (body && typeof body === 'object' && !(body instanceof FormData)) {
            if (!headers['Content-Type']) {
                headers['Content-Type'] = 'application/json';
            }
            if (headers['Content-Type'] === 'application/json') {
                 try {
                    body = JSON.stringify(body);
                 } catch (stringifyError) {
                     console.error("Failed to stringify body for authFetch:", stringifyError);
                     throw new Error("Invalid data provided for request body.");
                 }
            }
        }

        const fetchOptions = { ...options, headers, body, credentials: 'include' };
        const response = await fetch(fullUrl, fetchOptions);

        if (response.status === 401) {
            // If we had a token and got 401, handle the auth failure
            if (token) {
                const storedUser = getUserData();
                if (storedUser?.verification_status === 'unverified') {
                    console.warn('authFetch: Received 401 for an unverified user. Preserving tokens.');
                    throw new Error('Authentication failed. Please log in again.');
                } else {
                    console.debug('authFetch: Received 401 for a verified or unknown user. Clearing tokens.');
                    clearTokens();
                    throw new Error('Authentication failed. Please log in again.');
                }
            }
            // If we had no token, just return the 401 response — let the caller decide
        }

        return response;
    } catch (error) {
        // Only log if it's not an expected unauthenticated 401
        if (error.message !== 'Authentication failed. Please log in again.') {
            console.error(`Auth fetch error for ${fullUrl}:`, error);
        }
        if (error.message.includes('Authentication required')) {
            clearTokens();
        }
        throw error;
    }
};

export const updatePassword = async (currentPassword, newPassword) => {
    const url = AUTH_ENDPOINTS.UPDATE_PASSWORD;
    try {
        const response = await authFetch(url, {
            method: 'PUT',
            body: { current_password: currentPassword, new_password: newPassword },
        });

        if (!response.ok) {
            let errorMessage = 'Password update failed';
            try {
                const errorData = await response.json();
                errorMessage = errorData.detail || `Password update failed: ${response.status}`;
            } catch (e) { errorMessage = `Password update failed: ${response.status}`; }
            
            if (response.status === 401) { errorMessage = 'Incorrect current password.'; }
            throw new Error(errorMessage);
        }
        return await response.json();
    } catch (error) {
        console.error('Password update error:', error);
        throw error;
    }
};

export const requestPasswordReset = async (email) => {
    const url = AUTH_ENDPOINTS.REQUEST_PASSWORD_RESET;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Request failed: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Request password reset error:', error);
        throw error;
    }
};

export const verifyResetToken = async (token) => {
    const url = `${AUTH_ENDPOINTS.VERIFY_RESET_TOKEN}?token=${encodeURIComponent(token)}`;
    try {
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
        });
        if (response.status === 204) {
            return true;
        }
        return false;
    } catch (error) {
        console.error('Verify reset token error:', error);
        return false;
    }
};

export const resetPassword = async (token, newPassword) => {
    const url = AUTH_ENDPOINTS.RESET_PASSWORD;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, new_password: newPassword }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Password reset failed: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Reset password error:', error);
        throw error;
    }
};

export const trackNetworkError = (operationType, error) => {
  console.error(`[Network Error] Operation: ${operationType}`, {
    message: error.message,
    stack: error.stack,
    name: error.name,
  });
};

export default {
    login,
    register,
    logout,
    refreshToken,
    getCurrentUser,
    authFetch,
    updatePassword,
    requestPasswordReset,
    verifyResetToken,
    resetPassword,
    requestVerificationCode,
    verifyEmail,
    trackNetworkError,
};